/**
 * Shared helpers for the FoodPulse mobile pickers so every picker
 * (Add-Deal, Add-Vendor, Add-Product, Deals filter, Stores filter)
 * pulls from the same `/api/geo/cascade` endpoint that SocialPulse's
 * `locations` collection powers.
 *
 * Fallback: if the cascade returns fewer than 5 results (e.g., the
 * user picks an obscure country we haven't seeded yet), we fall back
 * to the static `US_STATES` / `COUNTRIES` datasets so the UI still
 * shows something. This is deliberately silent — no error toasts.
 */
import axios from "axios";
import { US_STATES } from "@/data/usStates";
import { COUNTRIES } from "@/data/countries";

const API = process.env.REACT_APP_BACKEND_URL;
const MIN_RESULTS = 5;

export async function fetchCountries(query = "") {
  try {
    const res = await axios.get(`${API}/api/geo/cascade`, {
      params: { kind: "country", q: query || undefined, limit: 500 },
    });
    const items = res.data.items || [];
    if (items.length >= MIN_RESULTS) {
      return items.map((i) => ({ value: i.name, label: i.name, slug: i.slug }));
    }
  } catch {/* fall through */}
  // Fallback — static list
  const q = (query || "").toLowerCase();
  return COUNTRIES
    .filter((c) => !q || c.name.toLowerCase().includes(q))
    .map((c) => ({ value: c.name, label: c.name }));
}

export async function fetchStates(country = "United States", query = "") {
  try {
    const res = await axios.get(`${API}/api/geo/cascade`, {
      params: { kind: "state", country, q: query || undefined, limit: 500 },
    });
    const items = res.data.items || [];
    if (items.length >= MIN_RESULTS) {
      return items.map((i) => ({ value: i.name, label: i.name, slug: i.slug }));
    }
  } catch {/* fall through */}
  // Fallback (US only — other countries just return empty)
  if (country !== "United States") return [];
  const q = (query || "").toLowerCase();
  return US_STATES
    .filter((s) => !q || s.name.toLowerCase().includes(q))
    .map((s) => ({ value: s.name, label: s.name }));
}

export async function fetchCities(country = "United States", state = "", query = "") {
  try {
    const res = await axios.get(`${API}/api/geo/cascade`, {
      params: { kind: "city", country, state: state || undefined, q: query || undefined, limit: 500 },
    });
    const items = res.data.items || [];
    if (items.length > 0) {
      return items.map((i) => ({ value: i.name, label: i.name, slug: i.slug }));
    }
  } catch {/* fall through */}
  // Fallback: query food_businesses for distinct cities in this state
  try {
    const res = await axios.get(`${API}/api/deals/cities`, {
      params: { state: state || undefined, q: query || undefined, limit: 300 },
    });
    return (res.data.items || []).map((c) => ({ value: c, label: c }));
  } catch {
    return [];
  }
}
